import React, { useState, useEffect, useCallback, useRef } from 'react';
import Panel from './Panel.jsx';

const COLORS = {
  primary: '#3b82f6',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  background: '#0f172a',
  cardBg: '#1e293b',
  text: '#f1f5f9',
  textMuted: '#94a3b8',
  border: '#334155',
};

// ── The staleness contract, on screen ────────────────────────────────────────
//
// The server decides the state; this only renders it. Both halves matter: a
// board that computes freshness client-side is measuring how recently the
// browser asked, not how recently the data was true — and those diverge exactly
// when it counts, which is when the feed behind the data has stopped.
//
// Each state carries a WORD, not just a color. A red board across the office is
// unreadable to a colorblind viewer and to any projector with bad color; the
// label is what actually communicates.
//
// The banner NAMES the panels that are behind. It used to say "everything below
// is stale — do not make calls on it" over a board whose numbers were all
// current, because one weekly panel was being judged on a one-hour clock. A
// warning that fires over healthy data is one the reader learns to click past,
// which costs exactly the outage the contract exists to catch.
const FRESHNESS_META = {
  live: { label: 'LIVE', color: COLORS.success, banner: null },
  late: {
    label: 'FEED LATE',
    color: COLORS.warning,
    banner: 'is older than its feed should be. The rest of the board is current.',
  },
  dark: {
    label: 'FEED DARK',
    color: COLORS.danger,
    banner: 'has stopped updating — do not make calls on it. The rest of the board is current.',
  },
  unknown: {
    label: 'UNDATED',
    color: COLORS.danger,
    banner: 'did not report when its data was generated, so its age cannot be checked.',
  },
};

// "Epic throughput and Work type mix" — the panels, not "some panels".
function namePanels(stalePanels = []) {
  const titles = stalePanels.map((p) => p.title).filter(Boolean);
  if (titles.length === 0) return 'Part of this board';
  if (titles.length === 1) return titles[0];
  if (titles.length === 2) return `${titles[0]} and ${titles[1]}`;
  return `${titles.slice(0, -1).join(', ')} and ${titles[titles.length - 1]}`;
}

function formatAge(seconds) {
  if (seconds == null) return 'unknown age';
  if (seconds < 90) return `${seconds}s old`;
  if (seconds < 5400) return `${Math.round(seconds / 60)}m old`;
  if (seconds < 172800) return `${Math.round(seconds / 3600)}h old`;
  return `${Math.round(seconds / 86400)}d old`;
}

// The age shown is the DATA's age, recomputed from its timestamp on a ticking
// clock — never "time since I fetched". A tab left open all weekend against a
// dead feed must say "3d old" and keep counting, not freeze at "12s old".
function useTickingAge(generatedAt) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 10_000);
    return () => clearInterval(t);
  }, []);
  if (!generatedAt) return null;
  return Math.max(0, Math.round((now - new Date(generatedAt).getTime()) / 1000));
}

function FreshnessBar({ freshnessState, generatedAt, staleCount = 0, onRefresh, refreshing }) {
  const meta = FRESHNESS_META[freshnessState] || FRESHNESS_META.unknown;
  const age = useTickingAge(generatedAt);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: 0.7,
          color: meta.color,
          border: `1px solid ${meta.color}`,
          borderRadius: 999,
          padding: '3px 11px',
        }}
      >
        <span
          style={{ width: 7, height: 7, borderRadius: '50%', background: meta.color, display: 'inline-block' }}
          aria-hidden="true"
        />
        {meta.label}
      </span>
      {/* The age shown is the OLDEST panel's, and it is just an age now — the
          board no longer concludes a feed is dead from that number alone, so
          printing one view-wide "stale after 60m" against it would be naming a
          threshold that no panel is actually judged by. */}
      <span style={{ fontSize: 12.5, color: COLORS.textMuted }}>
        {generatedAt ? `oldest panel ${formatAge(age)}` : 'no data age reported'}
        {staleCount > 0 ? ` · ${staleCount} panel${staleCount > 1 ? 's' : ''} behind` : ''}
      </span>
      {onRefresh && (
        <button
          onClick={onRefresh}
          disabled={refreshing}
          style={{
            background: 'transparent',
            border: `1px solid ${COLORS.border}`,
            color: COLORS.textMuted,
            borderRadius: 6,
            padding: '3px 10px',
            fontSize: 12,
            cursor: refreshing ? 'default' : 'pointer',
          }}
        >
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      )}
    </div>
  );
}

function Banner({ text, color }) {
  if (!text) return null;
  return (
    <div
      role="status"
      style={{
        background: `${color}1a`,
        border: `1px solid ${color}`,
        borderRadius: 8,
        padding: '11px 16px',
        color,
        fontSize: 13.5,
        fontWeight: 600,
      }}
    >
      {text}
    </div>
  );
}

// Panels keep their declared order but are grouped under their `group` label,
// so an exec view reads as sections rather than as a wall of undifferentiated
// tiles. A panel with no group falls into a single unlabeled run.
function groupPanels(panels) {
  const groups = [];
  for (const p of panels) {
    const name = p.group || '';
    const last = groups[groups.length - 1];
    if (last && last.name === name) last.panels.push(p);
    else groups.push({ name, panels: [p] });
  }
  return groups;
}

// The tab a dashboards hash names, or null: '#/dashboards/engineering/week'.
export function tabFromHash(hash = '') {
  const [section, key, tab] = String(hash).replace(/^#\/?/, '').split('/');
  return section === 'dashboards' && key && tab ? tab : null;
}

// Whether the address bar may be rewritten to the view on screen. Not while the
// hash already names somewhere else the reader may go: that is a navigation the
// shell has not caught up with, and rewriting the hash would cancel it. A key
// this caller cannot see is a stale link, which does get corrected.
export function addressMayFollow(hash = '', activeKey = null, views = []) {
  const [section, key] = String(hash).replace(/^#\/?/, '').split('/');
  if (section !== 'dashboards') return false;
  return !key || key === activeKey || !views.some((v) => v.key === key);
}

// The tabs a reader can pick from the tab row. A sub tab is reached from the
// panel that links to it, and has a way back to its parent instead.
export function mainTabs(tabs = []) {
  return tabs.filter((t) => t && t.id && !t.sub);
}

// Which tab is showing: the one asked for if the view has it, else the first.
export function resolveTab(tabs = [], requested = null) {
  if (!tabs.length) return null;
  if (tabs.some((t) => t.id === requested)) return requested;
  return mainTabs(tabs)[0]?.id || tabs[0].id;
}

// A panel with no tab sits on the first tab, so a panel added to a tabbed view
// by hand is never invisible.
export function panelsForTab(panels = [], tabs = [], tab = null) {
  if (!tabs.length || !tab) return panels;
  const first = mainTabs(tabs)[0]?.id || tabs[0].id;
  return panels.filter((p) => (p.tab && tabs.some((t) => t.id === p.tab) ? p.tab : first) === tab);
}

function TabRow({ tabs, active, onPick }) {
  const current = tabs.find((t) => t.id === active);
  const parent = current?.sub ? tabs.find((t) => t.id === current.parent) || mainTabs(tabs)[0] : null;
  return (
    <div role="tablist" style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${COLORS.border}`, marginBottom: 6, flexWrap: 'wrap' }}>
      {mainTabs(tabs).map((t) => {
        const on = t.id === active || (parent && t.id === parent.id);
        return (
          <button
            key={t.id}
            role="tab"
            aria-selected={t.id === active}
            onClick={() => onPick(t.id)}
            style={{
              background: 'transparent',
              border: 'none',
              borderBottom: `2px solid ${on ? COLORS.primary : 'transparent'}`,
              color: on ? COLORS.text : COLORS.textMuted,
              padding: '8px 14px',
              fontSize: 14,
              fontWeight: on ? 600 : 500,
              cursor: 'pointer',
              marginBottom: -1,
            }}
          >
            {t.label}
          </button>
        );
      })}
      {current?.sub && (
        <span style={{ alignSelf: 'center', marginLeft: 8, fontSize: 13, color: COLORS.textMuted }}>
          <button
            onClick={() => onPick(parent?.id)}
            style={{ background: 'transparent', border: 'none', color: COLORS.primary, cursor: 'pointer', fontSize: 13, padding: 0 }}
          >
            ← {parent?.label}
          </button>
          {' · '}
          {current.label}
        </span>
      )}
    </div>
  );
}

// `views` and `viewKey` arrive from the shell when the left nav is driving.
// Without them this component still owns its own list and switcher, so a
// wallboard — or any caller that renders it standalone — keeps working.
export default function Dashboards({
  onBack,
  views: viewsProp = null,
  viewKey = null,
  initialViewKey = null,
  initialTab = null,
  onViewChange = null,
}) {
  const navDriven = viewsProp !== null;
  const [ownViews, setViews] = useState([]);
  const views = navDriven ? viewsProp : ownViews;
  const [activeKey, setActiveKey] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [wallboard, setWallboard] = useState(false);
  const activeKeyRef = useRef(null);
  const [tab, setTab] = useState(initialTab);
  // A followed link moves the tab from outside, and so does leaving for another
  // view (its hash has no tab, so the next visit opens on the first one). Read
  // from the hash itself on every change, not from the shell's route: tab
  // clicks only ever REPLACE the address, so the route can still hold the tab
  // the page was opened on, and a link to that same tab would change nothing.
  useEffect(() => {
    const onHash = () => setTab(tabFromHash(window.location.hash));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    if (navDriven) return;
    fetch('/api/hub/views', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : r.json().then((e) => Promise.reject(new Error(e.error)))))
      .then((d) => {
        const list = d.views || [];
        setViews(list);
        // A deep-linked view wins, but only if it actually exists and this
        // caller may see it — a stale bookmark or a since-revoked role must
        // land somewhere real rather than on a permanently empty board.
        const requested = initialViewKey && list.some((v) => v.key === initialViewKey) ? initialViewKey : null;
        if (requested) setActiveKey(requested);
        else if (list.length) setActiveKey(list[0].key);
        else setLoading(false);
      })
      .catch((e) => {
        setError(e.message || 'Could not load views');
        setLoading(false);
      });
  }, [navDriven]);

  // Controlled mode: the nav owns the selection. Falling back to the first view
  // keeps a stale deep link from landing on a permanently empty board.
  useEffect(() => {
    if (!navDriven) return;
    const next = views.some((v) => v.key === viewKey) ? viewKey : views[0]?.key || null;
    setActiveKey(next);
    if (!next) setLoading(false);
  }, [navDriven, viewKey, views]);

  const load = useCallback(
    (key, { silent = false } = {}) => {
      if (!key) return;
      if (silent) setRefreshing(true);
      else setLoading(true);
      fetch(`/api/hub/views/${encodeURIComponent(key)}/data`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : r.json().then((e) => Promise.reject(new Error(e.error)))))
        .then((d) => {
          // A slow response for a view the user has since navigated away from
          // must not overwrite the one they are looking at.
          if (activeKeyRef.current !== key) return;
          setData(d);
          setError('');
        })
        .catch((e) => setError(e.message || 'Could not load this view'))
        .finally(() => {
          setLoading(false);
          setRefreshing(false);
        });
    },
    []
  );

  useEffect(() => {
    activeKeyRef.current = activeKey;
    setData(null);
    load(activeKey);
  }, [activeKey, load]);

  const tabs = Array.isArray(data?.view?.tabs) ? data.view.tabs : [];
  const activeTab = resolveTab(tabs, tab);

  // The address bar follows the view and the tab, so a copied link opens the
  // same page. Only once the view's tabs are known: before that, writing the
  // view alone would drop the tab a deep link asked for.
  useEffect(() => {
    if (!activeKey || !onViewChange || data?.view?.key !== activeKey) return;
    if (!addressMayFollow(window.location.hash, activeKey, views)) return;
    onViewChange(activeKey, activeTab);
  }, [activeKey, activeTab, data, onViewChange, views]);

  // Auto-refresh on the view's own cadence. The TV runs hot (60s); a view someone
  // opened deliberately does not need to.
  useEffect(() => {
    const seconds = data?.view?.refreshSeconds;
    if (!seconds || !activeKey) return;
    const t = setInterval(() => load(activeKey, { silent: true }), seconds * 1000);
    return () => clearInterval(t);
  }, [data?.view?.refreshSeconds, activeKey, load]);

  const isWallboard = wallboard || data?.view?.displayMode === 'wallboard';
  const freshnessState = data?.freshness?.state || 'unknown';
  const meta = FRESHNESS_META[freshnessState] || FRESHNESS_META.unknown;

  // The fraud-dashboard's rule, kept but aimed: a panel whose own feed is dark
  // dims to ~30%, which is legible as "do not read this" from across a room in a
  // way a banner is not, once someone has stopped noticing banners.
  //
  // What changed is the SCOPE. Dimming every tile because one of them is behind
  // greys out current numbers, and a board that greys out numbers that are fine
  // is a board people learn to read through. The dimming now lands on the tiles
  // it is true of.
  const staleById = new Map((data?.stalePanels || []).map((p) => [p.id, p.state]));
  const isDimmed = (p) => {
    const st = staleById.get(p.id);
    return st === 'dark' || st === 'unknown';
  };

  const activeView = views.find((v) => v.key === activeKey);

  return (
    <main
      style={{
        maxWidth: isWallboard ? 1680 : 1180,
        margin: '0 auto',
        padding: isWallboard ? '24px 28px' : '32px 24px',
      }}
    >
      {/* View switcher. The view BUTTONS are suppressed when the left nav is
          already the switcher — two controls for one selection is how they
          drift out of sync — but the container stays, because the TV-size
          toggle lives in it and is useful either way. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          marginBottom: 20,
        }}
      >
        {!navDriven && onBack && (
          <button
            onClick={onBack}
            style={{
              background: 'transparent',
              border: `1px solid ${COLORS.border}`,
              color: COLORS.textMuted,
              borderRadius: 6,
              padding: '5px 12px',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            ← Apps
          </button>
        )}
        {!navDriven && views.map((v) => {
          const active = v.key === activeKey;
          return (
            <button
              key={v.key}
              onClick={() => setActiveKey(v.key)}
              title={v.audience || ''}
              style={{
                background: active ? COLORS.primary : 'transparent',
                border: `1px solid ${active ? COLORS.primary : COLORS.border}`,
                color: active ? '#fff' : COLORS.textMuted,
                borderRadius: 6,
                padding: '5px 14px',
                fontSize: 13.5,
                fontWeight: active ? 600 : 500,
                cursor: 'pointer',
              }}
            >
              {v.name}
            </button>
          );
        })}
        <div style={{ flex: 1 }} />
        <button
          onClick={() => setWallboard((w) => !w)}
          style={{
            background: 'transparent',
            border: `1px solid ${COLORS.border}`,
            color: COLORS.textMuted,
            borderRadius: 6,
            padding: '5px 12px',
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          {isWallboard ? 'Normal size' : 'TV size'}
        </button>
      </div>

      {/* Title + freshness */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
        <div>
          <h2 style={{ fontSize: isWallboard ? 30 : 22, fontWeight: 700, color: COLORS.text, margin: 0 }}>
            {data?.view?.name || activeView?.name || 'Dashboards'}
          </h2>
          {(data?.view?.description || activeView?.description) && (
            <p style={{ fontSize: 13.5, color: COLORS.textMuted, margin: '5px 0 0' }}>
              {data?.view?.description || activeView?.description}
            </p>
          )}
        </div>
        {tabs.length > 0 && !isWallboard && (
          <TabRow tabs={tabs} active={activeTab} onPick={setTab} />
        )}
        {data && (
          <FreshnessBar
            freshnessState={freshnessState}
            generatedAt={data.generatedAt}
            staleCount={data.stalePanels?.length || 0}
            onRefresh={() => load(activeKey, { silent: true })}
            refreshing={refreshing}
          />
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Banner
          text={meta.banner ? `${namePanels(data?.stalePanels)} ${meta.banner}` : null}
          color={meta.color}
        />

        {/* Name the feeds that died. "Could not check" must never read as "zero". */}
        {data?.failedSources?.length > 0 && (
          <Banner
            color={COLORS.danger}
            text={`${data.failedSources.length} feed${data.failedSources.length > 1 ? 's' : ''} unreachable: ${data.failedSources
              .map((f) => f.source)
              .join(', ')}`}
          />
        )}

        {error && <Banner text={error} color={COLORS.danger} />}

        {loading && (
          <p style={{ color: COLORS.textMuted, textAlign: 'center', padding: '60px 0' }}>Loading…</p>
        )}

        {/* An empty view is a real, deliberate state here — Parvinder's view
            ships with no panels because its data source does not exist yet.
            Saying that plainly beats seeding tiles that would read "not
            measurable" forever until people stopped seeing that state at all. */}
        {!loading && data && data.panels.length === 0 && (
          <div
            style={{
              border: `1px dashed ${COLORS.border}`,
              borderRadius: 10,
              padding: '48px 24px',
              textAlign: 'center',
              color: COLORS.textMuted,
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 600, color: COLORS.text, marginBottom: 8 }}>
              No panels on this view yet
            </div>
            <div style={{ fontSize: 13.5, maxWidth: 560, margin: '0 auto', lineHeight: 1.55 }}>
              {data.view?.description}
            </div>
          </div>
        )}

        {!loading &&
          data &&
          groupPanels(isWallboard ? data.panels : panelsForTab(data.panels, tabs, activeTab)).map((group, gi) => (
            <section key={`${group.name}-${gi}`} style={{ marginTop: gi === 0 ? 4 : 14 }}>
              {group.name && (
                <h3
                  style={{
                    fontSize: isWallboard ? 15 : 12,
                    fontWeight: 700,
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                    color: COLORS.textMuted,
                    margin: '0 0 10px',
                  }}
                >
                  {group.name}
                </h3>
              )}
              {/* A panel's `size` was declared in the view data and then ignored
                  by this grid — every tile rendered the same width regardless.
                  Honoured now: 'xl' takes the full row (a hero metric with a
                  chart needs the width to be readable), 'lg' takes two columns.
                  A wallboard stays uniform: varying tile sizes across a room
                  reads as a broken layout, not as emphasis. */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(auto-fill, minmax(${isWallboard ? 300 : 232}px, 1fr))`,
                  gap: isWallboard ? 18 : 14,
                }}
              >
                {group.panels.map((p) => (
                  <div
                    key={p.id}
                    style={{
                      gridColumn: isWallboard
                        ? undefined
                        : p.size === 'xl'
                          ? '1 / -1'
                          : p.size === 'lg'
                            ? 'span 2'
                            : undefined,
                      minWidth: 0,
                    }}
                  >
                    <Panel panel={p} wallboard={isWallboard} dimmed={isDimmed(p)} />
                    {p.more && tabs.some((t) => t.id === p.more) && !isWallboard && (
                      <button
                        onClick={() => setTab(p.more)}
                        style={{ background: 'transparent', border: 'none', color: COLORS.primary, cursor: 'pointer', fontSize: 13, padding: '6px 2px' }}
                      >
                        {tabs.find((t) => t.id === p.more).label} →
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </section>
          ))}
      </div>
    </main>
  );
}
